import { inject, Injectable } from '@angular/core';
import { signalStore, withState, withMethods, } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { catchError, map, of, pipe, switchMap, tap } from 'rxjs';
import { ELoadingEntity } from 'src/models/generic-store.model';
import { withDevtools, updateState } from '@angular-architects/ngrx-toolkit';
import { RestApiService } from 'src/services/rest-api.service';
import { baseState, EFaqPageCode } from 'src/models/common.model';
import { IFaqState } from 'src/models/faq.model';
import { withError, withLoading, withoutError, withoutLoading, withRequestStatus } from '../store-features/with-request-status.store-feature';

enum EFaqActions {
  FAQS_PAGE_REQUEST= '[Faq] Faqs Page Request',
  FAQS_PAGE_SUCCESS = '[Faq] Faqs Page Success',
  FAQS_PAGE_FAILURE = '[Faq] Faqs Page Failure',
  CATEGORIES_REQUEST = '[Faq] Categories Request',
  CATEGORIES_SUCCESS = '[Faq] Categories Success',
  CATEGORIES_FAILURE = '[Faq] Categories Failure',
  CATEGORY_DETAIL_REQUEST = '[Faq] Category Detail Request',
  CATEGORY_DETAIL_SUCCESS = '[Faq] Category Detail Success',
  CATEGORY_DETAIL_FAILURE = '[Faq] Category Detail Failure',
  SEARCH_REQUEST = '[Faq] Search Request',
  SEARCH_SUCCESS = '[Faq] Search Success',
  SEARCH_FAILURE = '[Faq] Search Failure',
  RESET_STATE = '[Faq] Reset State',
}

const initialFaqState: IFaqState = {
  pageFaqs: null,
  categories: [],
  categoryDetail: null,
  searchResults: [],
};

@Injectable({ providedIn: 'root' })
export class FaqStore extends signalStore(
  withDevtools('faq'),
  withState(initialFaqState),
  withRequestStatus(),
  withMethods((store, restApiService = inject(RestApiService)) => {

    const getPageFaqs = rxMethod<{ pageCode: EFaqPageCode }>(
      pipe(
        tap(() =>
          updateState(store, EFaqActions.FAQS_PAGE_REQUEST, ({ loadingProps }) => ({
            ...withLoading(ELoadingEntity.PAGE_FAQS, loadingProps),
          }))
        ),
        switchMap(({ pageCode }) =>
          restApiService.getFaqsByPageCode(pageCode).pipe(
            map(response =>
              updateState(store, EFaqActions.FAQS_PAGE_SUCCESS, ({ loadingProps, errorProps }) => ({
                pageFaqs: response,
                ...withoutLoading(ELoadingEntity.PAGE_FAQS, loadingProps),
                ...withoutError(ELoadingEntity.PAGE_FAQS, errorProps),
              }))
            ),
            catchError((error) =>
              of(
                updateState(store, EFaqActions.FAQS_PAGE_FAILURE, ({ loadingProps, errorProps }) => ({
                  ...withoutLoading(ELoadingEntity.PAGE_FAQS, loadingProps),
                  ...withError(ELoadingEntity.PAGE_FAQS, { error, errorParams: { faqPageCode: pageCode } }, errorProps),
                }))
              )
            )
          )
        )
      )
    );

    const getCategories = rxMethod<void>(
      pipe(
        tap(() =>
          updateState(store, EFaqActions.CATEGORIES_REQUEST, ({ loadingProps }) => ({
            ...withLoading(ELoadingEntity.FAQ_CATEGORIES, loadingProps),
          }))
        ),
        switchMap(() =>
          restApiService.getFaqCategories().pipe(
            map(response =>
              updateState(store, EFaqActions.CATEGORIES_SUCCESS, ({ loadingProps, errorProps }) => ({
                categories: response,
                ...withoutLoading(ELoadingEntity.FAQ_CATEGORIES, loadingProps),
                ...withoutError(ELoadingEntity.FAQ_CATEGORIES, errorProps),
              }))
            ),
            catchError((error) =>
              of(
                updateState(store, EFaqActions.CATEGORIES_FAILURE, ({ loadingProps, errorProps }) => ({
                  ...withoutLoading(ELoadingEntity.FAQ_CATEGORIES, loadingProps),
                  ...withError(ELoadingEntity.FAQ_CATEGORIES, { error, errorParams: {} }, errorProps),
                }))
              )
            )
          )
        )
      )
    );

    const getCategoryDetail = rxMethod<{ categoryId: number }>(
      pipe(
        tap(() =>
          updateState(store, EFaqActions.CATEGORY_DETAIL_REQUEST, ({ loadingProps }) => ({
            ...withLoading(ELoadingEntity.FAQ_CATEGORY_DETAIL, loadingProps),
          }))
        ),
        switchMap(({ categoryId }) =>
          restApiService.getFaqCategory(categoryId).pipe(
            map(response =>
              updateState(store, EFaqActions.CATEGORY_DETAIL_SUCCESS, ({ loadingProps, errorProps }) => ({
                categoryDetail: response,
                ...withoutLoading(ELoadingEntity.FAQ_CATEGORY_DETAIL, loadingProps),
                ...withoutError(ELoadingEntity.FAQ_CATEGORY_DETAIL, errorProps),
              }))
            ),
            catchError((error) =>
              of(
                updateState(store, EFaqActions.CATEGORY_DETAIL_FAILURE, ({ loadingProps, errorProps }) => ({
                  ...withoutLoading(ELoadingEntity.FAQ_CATEGORY_DETAIL, loadingProps),
                  ...withError(ELoadingEntity.FAQ_CATEGORY_DETAIL, { error, errorParams: { categoryId } }, errorProps),
                }))
              )
            )
          )
        )
      )
    );

    const searchFaqs = rxMethod<{ query: string }>(
      pipe(
        tap(() =>
          updateState(store, EFaqActions.SEARCH_REQUEST, ({ loadingProps }) => ({
            ...withLoading(ELoadingEntity.FAQ_SEARCH, loadingProps),
          }))
        ),
        switchMap(({ query }) =>
          restApiService.searchFaqs(query).pipe(
            map(response =>
              updateState(store, EFaqActions.SEARCH_SUCCESS, ({ loadingProps, errorProps }) => ({
                searchResults: response,
                ...withoutLoading(ELoadingEntity.FAQ_SEARCH, loadingProps),
                ...withoutError(ELoadingEntity.FAQ_SEARCH, errorProps),
              }))
            ),
            catchError((error) =>
              of(
                updateState(store, EFaqActions.SEARCH_FAILURE, ({ loadingProps, errorProps }) => ({
                  ...withoutLoading(ELoadingEntity.FAQ_SEARCH, loadingProps),
                  ...withError(ELoadingEntity.FAQ_SEARCH, { error, errorParams: { query } }, errorProps),
                }))
              )
            )
          )
        )
      )
    );

    const resetState = () => {
      updateState(store, EFaqActions.RESET_STATE, { ...initialFaqState, ...baseState });
    }

    return {
      getPageFaqs,
      getCategories,
      getCategoryDetail,
      searchFaqs,
      resetState,
    };
  })
) {}